import React, { useId } from "react";
import { Grid, MenuItem, TextField } from "@mui/material";
import { SeccionFormulario } from "../../../components/base";
import EditorPermisos from "./EditorPermisos";

/**
 * @param {{rolName: string, idRolSuperior: number|string}} datos
 * @param {Array} [roles] Roles activos: un superior que ya no está entre ellos (fue borrado) obliga a elegir otro.
 * @returns {Object<string, string>} errores por campo; vacío si todo está bien.
 */
export const validarRol = (datos, roles) => {
  const errores = {};
  if (String(datos.rolName ?? "").trim().length === 0) errores.rolName = "Ingrese el nombre del rol";
  if (datos.idRolSuperior === "" || datos.idRolSuperior === null || datos.idRolSuperior === undefined) {
    errores.idRolSuperior = "Seleccione el rol superior";
  } else if (roles && !roles.some((r) => r.id === datos.idRolSuperior)) {
    errores.idRolSuperior = "El rol superior actual fue borrado: elija otro";
  }
  return errores;
};

/**
 * Nombre, rol superior y permisos de un rol (alta y edición).
 * @param {object} props
 * @param {{rolName: string, idRolSuperior: number|string}} props.datos
 * @param {Function} props.onCampo (campo, valor) => void
 * @param {Object<string, boolean>} props.permisos
 * @param {Function} props.onPermisos
 * @param {Function} [props.onTodo] Marcar o quitar todo (con confirmación del padre).
 * @param {Array} props.roles Roles activos, para el rol superior.
 * @param {object} props.errores
 * @param {boolean} [props.deshabilitado]
 * @param {number} [props.idPropio] En la edición: el rol que se edita (un rol raíz es su propio superior).
 * @param {boolean} [props.esRaiz] El rol que se edita es raíz (su superior es él mismo): solo entonces
 *   aparece él mismo en la lista de superiores.
 */
const FormularioRol = ({
  datos,
  onCampo,
  permisos,
  onPermisos,
  onTodo,
  roles,
  errores,
  deshabilitado = false,
  idPropio,
  esRaiz = false,
}) => {
  const prefijo = useId();
  const superiorEnLista = roles.some((r) => r.id === datos.idRolSuperior);
  const superiorBorrado = datos.idRolSuperior !== "" && !superiorEnLista;
  const eligioASiMismo = idPropio !== undefined && datos.idRolSuperior === idPropio;
  // Un rol que no es raíz no puede ser su propio superior.
  const opciones = roles.filter((r) => esRaiz || r.id !== idPropio);

  return (
    <>
      <SeccionFormulario titulo="Datos del rol">
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              id={`${prefijo}-rolName`}
              label="Nombre del rol"
              value={datos.rolName}
              onChange={(e) => onCampo("rolName", e.target.value.toUpperCase())}
              error={Boolean(errores.rolName)}
              helperText={errores.rolName || "Por ejemplo GESTOR DE CREDITO"}
              disabled={deshabilitado}
              inputProps={{ autoComplete: "off" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              required
              id={`${prefijo}-idRolSuperior`}
              label="Rol superior"
              value={superiorEnLista ? datos.idRolSuperior : ""}
              onChange={(e) => onCampo("idRolSuperior", e.target.value)}
              error={Boolean(errores.idRolSuperior) || superiorBorrado}
              helperText={
                (superiorBorrado ? "El rol superior actual fue borrado: elija otro" : errores.idRolSuperior) ||
                (eligioASiMismo
                  ? "Es un rol raíz: no tiene otro rol por encima"
                  : "El rol queda un nivel por debajo de este; sus usuarios tienen de jefe a usuarios de niveles más altos")
              }
              disabled={deshabilitado}
            >
              <MenuItem value="">
                <em>Seleccione el rol superior</em>
              </MenuItem>
              {opciones.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.id === idPropio ? `${r.rolName} (este mismo rol)` : r.rolName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </SeccionFormulario>

      <SeccionFormulario
        titulo="Permisos"
        descripcion="Lo que pueden ver y hacer los usuarios con este rol. Los cambios se aplican al guardar."
      >
        <EditorPermisos
          permisos={permisos}
          onCambiar={onPermisos}
          onTodo={onTodo}
          deshabilitado={deshabilitado}
        />
      </SeccionFormulario>
    </>
  );
};

export default FormularioRol;
